import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, KineticHeading } from "@/components/Motion";

// Reusable eyebrow / overline
export const Overline = ({ children, className = "" }) => (
  <span className={`overline text-gold flex items-center gap-3 ${className}`}>
    <span className="w-8 h-px bg-gold inline-block" /> {children}
  </span>
);

// Primary sharp-edged button
export const PrimaryButton = ({ to, children, testid, variant = "navy", className = "" }) => {
  const styles = {
    navy: "bg-navy text-white hover:bg-gold",
    gold: "bg-gold text-navy hover:bg-navy hover:text-white",
    outline: "border border-navy text-navy hover:bg-navy hover:text-white",
    outlineLight: "border border-white/40 text-white hover:bg-white hover:text-navy",
  };
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`inline-flex items-center gap-2 font-600 text-sm px-8 py-4 transition-colors duration-300 group ${styles[variant]} ${className}`}
    >
      {children}
      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
    </Link>
  );
};

// Inner-page hero band (navy)
export const PageHero = ({ eyebrow, title, subtitle, image }) => (
  <section className="relative bg-navy text-white overflow-hidden pt-32 pb-20 md:pt-44 md:pb-28">
    {image && (
      <div className="absolute inset-0">
        <img src={image} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-navy/60" />
      </div>
    )}
    <div className="grain absolute inset-0 opacity-30" />
    <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
      <Reveal><Overline>{eyebrow}</Overline></Reveal>
      <h1 className="mt-6 font-display font-800 tracking-tightest text-4xl sm:text-5xl lg:text-6xl leading-[0.98] max-w-4xl">
        <KineticHeading lines={Array.isArray(title) ? title : [title]} />
      </h1>
      {subtitle && (
        <Reveal delay={0.3}>
          <p className="mt-8 text-white/70 font-body text-base md:text-lg leading-relaxed max-w-2xl">{subtitle}</p>
        </Reveal>
      )}
    </div>
  </section>
);

// Final CTA band reused across pages
export const CTABand = () => (
  <section className="bg-lightgray py-24 md:py-32 relative overflow-hidden" data-testid="cta-band">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
      <div className="bg-navy relative overflow-hidden">
        <div className="grain absolute inset-0 opacity-30" />
        <div className="relative px-8 py-16 md:px-20 md:py-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <div className="max-w-2xl">
            <Overline>Let&apos;s Talk</Overline>
            <h2 className="mt-5 font-display font-800 tracking-tightest text-white text-3xl md:text-5xl leading-tight">
              Let&apos;s Build Something<br /> Better Together
            </h2>
            <p className="mt-6 text-white/70 font-body max-w-lg leading-relaxed">
              Partner with a firm engineered for organizations that demand excellence. Schedule a consultation and let&apos;s map your next move.
            </p>
          </div>
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <Link to="/contact" data-testid="cta-band-button" className="inline-flex items-center gap-3 bg-gold text-navy font-700 text-base px-10 py-5 hover:bg-white transition-colors duration-300 group">
              Schedule a Consultation
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

export const SectionHeading = ({ eyebrow, title, intro, align = "left", light = false }) => (
  <div className={`${align === "center" ? "text-center mx-auto" : ""} max-w-3xl`}>
    <Reveal><Overline className={align === "center" ? "justify-center" : ""}>{eyebrow}</Overline></Reveal>
    <Reveal delay={0.1}>
      <h2 className={`mt-5 font-display font-800 tracking-tightest text-3xl md:text-5xl leading-[1.02] ${light ? "text-white" : "text-navy"}`}>{title}</h2>
    </Reveal>
    {intro && (
      <Reveal delay={0.2}>
        <p className={`mt-6 font-body text-base md:text-lg leading-relaxed ${light ? "text-white/70" : "text-gray-600"}`}>{intro}</p>
      </Reveal>
    )}
  </div>
);
